<template>
  <div class="main-setting-page">
    <div class="wrapper-x-40">
      <h1>{{$t('admin.views.settings.h1')}}</h1>
    </div>
    <div class="settings-sticky">
      <div class="settings-sticky__bar wrapper-x-40">
        <button
          v-if="licensed"
          @click="saveData"
          type="button"
          class="btn btn-small btn-green"
        >
          {{$t('main.button_save')}}
        </button>
      </div>
      <div class="tabs">
        <div class="wrapper-x-40">
          <button
            :class="{ active: tab == 'system' }"
            class="focus:outline-none"
            @click="setTab('system')"
          >
            {{$t('admin.views.settings.tab_system')}}
          </button>
          <button
            :class="{ active: tab == 'access' }"
            class="focus:outline-none"
            @click="setTab('access')"
          >
            {{$t('admin.views.settings.tab_access')}}
          </button>
          <button
            :class="{ active: tab == 'mail' }"
            class="focus:outline-none"
            @click="setTab('mail')"
          >
            {{$t('admin.views.settings.tab_mail')}}
          </button>
          <button
            :class="{ active: tab == 'general' }"
            class="focus:outline-none"
            @click="setTab('general')"
          >
            {{$t('admin.views.settings.tab_general')}}
          </button>
          <button
            :class="{ active: tab == 'user-setting' }"
            class="focus:outline-none"
            @click="setTab('user-setting')"
          >
            {{$t('admin.views.settings.tab_user')}}
          </button>
          <button
            :class="{ active: tab == 'display' }"
            class="focus:outline-none"
            @click="setTab('display')"
          >
            {{$t('admin.views.settings.tab_display')}}
          </button>
          <button
            :class="{ active: tab == 'commerce' }"
            class="focus:outline-none"
            @click="setTab('commerce')"
          >
            {{$t('admin.views.settings.tab_commerce')}}
          </button>
          <button
            :class="{ active: tab == 'company' }"
            class="focus:outline-none"
            @click="setTab('company')"
          >
            {{$t('admin.views.settings.tab_company')}}
          </button>
        </div>
      </div>
    </div>
    <div class="content">
      <div class="wrapper-x-40">
        <div v-if="tab == 'system'">
          <h2 class="text-lg font-semibold mb-4">{{$t('admin.views.settings.tab_system')}}</h2>
          <div class="form w-1/3 text-sm">
            <div class="form-group">
              <label for="system_name">{{$t('admin.views.settings.system_name')}}:</label>
              <div>
                <input
                  id="system_name"
                  type="text"
                  v-model="form.system_name"
                />
              </div>
              <ValidationErrors :errors="errorsFor('system_name')" />
            </div>
            <div class="form-group">
              <label for="system-language">{{$t('admin.views.settings.language_interface')}}:</label>
              <div>
                <select
                  v-model="form.language"
                  id="system-language"
                >
                  <option
                    :value="lg.code"
                    v-for="lg in systemLanguages"
                    :key="`lg-${lg.code}`"
                  >
                    {{ lg.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label for="timezone">{{$t('admin.views.settings.timezone')}}:</label>
              <div>
                <select
                  id="timezone"
                  v-model="form.timezone"
                >
                  <option
                    :value="tz.code"
                    v-for="tz in timezones"
                    :key="`tz-${tz.code}`"
                  >
                    {{ tz.name }} ({{ tz.timezone }})
                  </option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label for="per_page">{{$t('admin.views.settings.length_of_list')}}:</label>
              <div>
                <select
                  id="per_page"
                  v-model="form.per_page"
                  style="width: auto"
                >
                  <option
                    :value="pp"
                    v-for="pp in perPages"
                    :key="`pp-${pp}`"
                  >
                    {{ pp }}
                  </option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label for="request_time_limit">{{$t('admin.views.settings.request_time_limit')}}:</label>
              <div>
                <input
                  id="request_time_limit"
                  type="number"
                  min="1"
                  max="600"
                  v-model="form.request_time_limit"
                />
              </div>
              <ValidationErrors :errors="errorsFor('request_time_limit')" />
            </div>
          </div>
        </div>
        <div v-if="tab == 'access'">
          <h2 class="text-lg font-semibold mb-4">{{$t('admin.views.settings.tab_access')}}</h2>
          <div class="form w-1/3 text-sm">
            <div class="form-group">
              <label for="admin_path">{{$t('admin.views.settings.security_way_to_admin')}}:</label>
              <div>
                <input
                  id="admin_path"
                  type="text"
                  v-model="form.admin_path"
                />
              </div>
              <ValidationErrors :errors="errorsFor('admin_path')" />
            </div>
            <div class="form-group">
              <label for="login">E-mail:</label>
              <div>
                <input
                  id="login"
                  type="text"
                  v-model="form.login"
                />
              </div>
              <ValidationErrors :errors="errorsFor('login')" />
            </div>
            <div class="form-group">
              <label for="password">{{$t('admin.views.settings.label_password')}}:</label>
              <div>
                <input
                  id="password"
                  type="password"
                  v-model="form.password"
                />
              </div>
              <ValidationErrors :errors="errorsFor('password')" />
            </div>
            <div class="form-group">
              <label for="password_confirmation">{{$t('admin.views.settings.label_confirm_password')}}:</label>
              <div>
                <input
                  id="password_confirmation"
                  type="password"
                  v-model="form.password_confirmation"
                />
              </div>
              <ValidationErrors :errors="errorsFor('password_confirmation')" />
            </div>
          </div>
        </div>
        <div v-if="tab == 'mail'">
          <h2 class="text-lg font-semibold mb-4">{{$t('admin.views.settings.tab_mail')}}</h2>
          <div class="form w-1/3 text-sm">
            <div class="form-group">
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.mail_send_to_client_enabled =
                      !form.mail_send_to_client_enabled
                  "
                >
                  <span class="check"><i
                      v-if="form.mail_send_to_client_enabled == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.mail_send_to_client_enabled')}}</span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <label for="email_notification">{{$t('admin.views.settings.mail_for_notify')}}:</label>
              <div>
                <input
                  id="email_notification"
                  type="text"
                  v-model="form.email_notification"
                />
              </div>
              <ValidationErrors :errors="errorsFor('email_notification')" />
            </div>

            <div
              class="form-group"
              v-if="
                !form.email_notification_additional ||
                form.email_notification_additional.length < 5
              "
            >
              <button
                type="button"
                class="text-green-600 underline text-sm"
                @click.prevent="addEmailNotificationRecipient"
              >
                {{$t('admin.views.settings.add_email_recipient')}}
              </button>
            </div>

            <div
              v-for="(email, index) in form.email_notification_additional"
              :key="`email_notification_additional_${index}`"
              class="form-group"
            >
              <div class="flex items-center gap-2">
                <div class="flex-1">
                  <input
                    type="text"
                    :id="`email_notification_additional_${index}`"
                    v-model="form.email_notification_additional[index]"
                    class="w-full"
                  />
                </div>
                <button
                  type="button"
                  class="w-8 h-8 flex items-center justify-center bg-red-600 rounded"
                  @click.prevent="removeEmailNotificationRecipient(index)"
                >
                  <span class="text-white text-xl leading-none">&times;</span>
                </button>
              </div>
              <ValidationErrors
                :errors="errorsFor(`email_notification_additional.${index}`)"
              />
            </div>

            <div class="form-group">
              <label for="email_title">{{$t('admin.views.settings.email_title')}}:</label>
              <div>
                <input
                  id="email_title"
                  type="text"
                  v-model="form.email_title"
                />
              </div>
              <ValidationErrors :errors="errorsFor('email_title')" />
            </div>

            <div class="form-group">
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.send_copy_order_for_client =
                      !form.send_copy_order_for_client
                  "
                >
                  <span class="check"><i
                      v-if="form.send_copy_order_for_client == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.label_send_user_copy_order')}}</span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <label for="sender">{{$t('admin.views.settings.sender')}}:</label>
              <div>
                <input
                  id="sender"
                  type="text"
                  v-model="form.sender"
                />
              </div>
              <ValidationErrors :errors="errorsFor('sender')" />
            </div>
            <div class="form-group">
              <label for="email_site">{{$t('admin.views.settings.label_email_site')}}:</label>
              <div>
                <input
                  id="email_site"
                  type="text"
                  v-model="form.email_site"
                />
              </div>
              <ValidationErrors :errors="errorsFor('email_site')" />
            </div>
            <div class="form-group">
              <label for="email_theme">{{$t('admin.views.settings.email_theme')}}:</label>
              <div>
                <input
                  id="email_theme"
                  type="text"
                  v-model="form.email_theme"
                />
              </div>
              <ValidationErrors :errors="errorsFor('email_theme')" />
            </div>

            <div class="mt-6">
              <h3 class="text-md font-semibold mb-4">{{$t('admin.views.settings.mail_delivery_settings')}}</h3>
              <div class="form-group">
                <label for="mail_transport">{{$t('admin.views.settings.mail_transport')}}:</label>
                <div>
                  <select
                    id="mail_transport"
                    v-model="form.mail_transport"
                  >
                    <option value="php_mail">{{$t('admin.views.settings.mail_transport_php_mail')}}</option>
                    <option value="sendmail">{{$t('admin.views.settings.mail_transport_sendmail')}}</option>
                    <option value="smtp">{{$t('admin.views.settings.mail_transport_smtp')}}</option>
                    <option value="log">{{$t('admin.views.settings.mail_transport_log')}}</option>
                  </select>
                </div>
                <ValidationErrors :errors="errorsFor('mail_transport')" />
              </div>

              <div
                v-if="form.mail_transport == 'sendmail'"
                class="form-group"
              >
                <label for="sendmail_path">{{$t('admin.views.settings.sendmail_path')}}:</label>
                <div>
                  <input
                    id="sendmail_path"
                    type="text"
                    v-model="form.sendmail_path"
                  />
                </div>
                <ValidationErrors :errors="errorsFor('sendmail_path')" />
              </div>

              <div v-if="form.mail_transport == 'smtp'">
                <div class="form-group">
                  <label for="smtp_host">{{$t('admin.views.settings.smtp_server')}}:</label>
                  <div>
                    <input
                      id="smtp_host"
                      type="text"
                      v-model="form.smtp_host"
                    />
                  </div>
                  <ValidationErrors :errors="errorsFor('smtp_host')" />
                </div>
                <div class="form-group">
                  <label for="smtp_port">{{$t('admin.views.settings.smtp_port')}}:</label>
                  <div>
                    <input
                      id="smtp_port"
                      type="number"
                      v-model="form.smtp_port"
                    />
                  </div>
                  <ValidationErrors :errors="errorsFor('smtp_port')" />
                </div>
                <div class="form-group">
                  <label for="smtp_encryption">{{$t('admin.views.settings.smtp_encryption')}}:</label>
                  <div>
                    <select
                      id="smtp_encryption"
                      v-model="form.smtp_encryption"
                    >
                      <option value="none">{{$t('admin.views.settings.smtp_encryption_none')}}</option>
                      <option value="ssl">{{$t('admin.views.settings.smtp_encryption_ssl')}}</option>
                      <option value="tls">{{$t('admin.views.settings.smtp_encryption_tls')}}</option>
                    </select>
                  </div>
                  <ValidationErrors :errors="errorsFor('smtp_encryption')" />
                </div>

                <div class="form-group">
                  <div>
                    <button
                      class="
                        rounded
                        flex
                        justify-center
                        items-center
                        focus:outline-none
                      "
                      @click="form.smtp_auth = !form.smtp_auth"
                    >
                      <span class="check"><i
                          v-if="form.smtp_auth == '1'"
                          class="fas fa-check text-red-500"
                        ></i></span>
                      <span class="ml-2 text-sm">{{$t('admin.views.settings.smtp_auth')}}</span>
                    </button>
                  </div>
                  <ValidationErrors :errors="errorsFor('smtp_auth')" />
                </div>

                <div v-if="form.smtp_auth == '1'">
                  <div class="form-group">
                    <label for="smtp_username">{{$t('admin.views.settings.smtp_username')}}:</label>
                    <div>
                      <input
                        id="smtp_username"
                        type="text"
                        v-model="form.smtp_username"
                      />
                    </div>
                    <ValidationErrors :errors="errorsFor('smtp_username')" />
                  </div>
                  <div class="form-group">
                    <label for="smtp_password">{{$t('admin.views.settings.smtp_password')}}:</label>
                    <div>
                      <input
                        id="smtp_password"
                        type="password"
                        v-model="form.smtp_password"
                      />
                    </div>
                    <ValidationErrors :errors="errorsFor('smtp_password')" />
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-6">
              <h3 class="text-md font-semibold mb-4">{{$t('admin.views.settings.mail_test')}}</h3>
              <div class="form-group">
                <label for="mail_test_customer_email">{{$t('admin.views.settings.mail_test_customer_email')}}:</label>
                <div>
                  <input
                    id="mail_test_customer_email"
                    type="text"
                    v-model="form.mail_test_customer_email"
                  />
                </div>
                <ValidationErrors :errors="errorsFor('mail_test_customer_email')" />
              </div>
              <div class="form-group">
                <button
                  type="button"
                  class="bg-blue-500 text-white px-4 py-2 rounded border"
                  @click.prevent="sendTestMail"
                >
                  {{$t('admin.views.settings.mail_test_send_button')}}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="tab == 'general'">
          <h2 class="text-lg font-semibold mb-4">{{$t('admin.views.settings.tab_general')}}</h2>
          <h3 class="text-md font-semibold mb-4">{{$t('admin.views.settings.plot')}}</h3>
          <div class="form-group flex items-baseline gap-2">
            <input
              type="radio"
              name="plot_mode"
              v-model="form.plot_mode"
              value="list"
              id="plot_mode_list"
            />
            <label for="plot_mode_list">{{$t('admin.views.settings.plot_mode_list')}}</label>
          </div>
          <div></div>
          <div class="form-group flex items-baseline gap-2">
            <input
              type="radio"
              name="plot_mode"
              v-model="form.plot_mode"
              value="custom"
              id="plot_mode_custom"
            />
            <label for="plot_mode_custom">{{$t('admin.views.settings.plot_mode_custom')}}</label>
          </div>
          <div></div>
          <div
            v-if="form.plot_mode=='custom'"
            class="mb-4"
          >
            <div class="plot-size-line flex items-end gap-3">
              <div class="form-group">
                <label class="text-sm text-gray-500">{{$t('admin.views.settings.plot_length_min')}}</label>
                <input
                  type="number"
                  style="width: 80px"
                  v-model="form.plot_length_min"
                  required
                />
              </div>
              <div class="mb-4 border-b w-10 border-black"></div>
              <div class="form-group">
                <label class="text-sm text-gray-500">{{$t('admin.views.settings.plot_length_max')}}</label>
                <input
                  type="number"
                  style="width: 80px"
                  v-model="form.plot_length_max"
                  required
                />
              </div>
              <div class="mb-2">{{$t('admin.views.settings.plot_diapozon_length')}}</div>
            </div>
            <div class="mt-2 plot-size-line flex items-end gap-3">
              <div class="form-group">
                <label class="text-sm text-gray-500">{{$t('admin.views.settings.plot_width_min')}}</label>
                <input
                  type="number"
                  style="width: 80px"
                  v-model="form.plot_width_min"
                  required
                />
              </div>
              <div class="mb-4 border-b w-10 border-black"></div>
              <div class="form-group">
                <label class="text-sm text-gray-500">{{$t('admin.views.settings.plot_width_max')}}</label>
                <input
                  type="number"
                  style="width: 80px"
                  v-model="form.plot_width_max"
                  required
                />
              </div>
              <div class="mb-2">{{$t('admin.views.settings.plot_diapozon_width')}}</div>
            </div>
            <div class="line-default mt-2">
              <div>{{$t('admin.views.settings.plot_default_size')}}</div>
              <div class="plot-size-line flex items-end gap-3">
                <div class="form-group">
                  <label class="text-sm text-gray-500">{{$t('admin.views.settings.plot_length_default')}}</label>
                  <input
                    type="number"
                    style="width: 80px"
                    v-model="form.plot_length_default"
                    required
                  />
                </div>
                <div class="mb-4 border-b w-10 border-black"></div>
                <div class="form-group">
                  <label class="text-sm text-gray-500">{{$t('admin.views.settings.plot_width_default')}}</label>
                  <input
                    type="number"
                    style="width: 80px"
                    v-model="form.plot_width_default"
                    required
                  />
                </div>
              </div>
            </div>

          </div>
          <div class="form-group flex items-baseline gap-2">
            <input
              type="radio"
              name="plot_mode"
              v-model="form.plot_mode"
              value="list_and_custom"
              id="plot_mode_list_and_custom"
            />
            <label for="plot_mode_list_and_custom">{{$t('admin.views.settings.plot_mode_list_and_custom')}}</label>
          </div>
          <div
            v-if="form.plot_mode=='list_and_custom'"
            class="mb-4"
          >
            <div class="plot-size-line flex items-end gap-3">
              <div class="form-group">
                <label class="text-sm text-gray-500">{{$t('admin.views.settings.plot_length_min')}}</label>
                <input
                  type="number"
                  style="width: 80px"
                  v-model="form.plot_length_min"
                  required
                />
              </div>
              <div class="mb-4 border-b w-10 border-black"></div>
              <div class="form-group">
                <label class="text-sm text-gray-500">{{$t('admin.views.settings.plot_length_max')}}</label>
                <input
                  type="number"
                  style="width: 80px"
                  v-model="form.plot_length_max"
                  required
                />
              </div>
              <div class="mb-2">{{$t('admin.views.settings.plot_diapozon_length')}}</div>
            </div>
            <div class="mt-2 plot-size-line flex items-end gap-3">
              <div class="form-group">
                <label class="text-sm text-gray-500">{{$t('admin.views.settings.plot_width_min')}}</label>
                <input
                  type="number"
                  style="width: 80px"
                  v-model="form.plot_width_min"
                  required
                />
              </div>
              <div class="mb-4 border-b w-10 border-black"></div>
              <div class="form-group">
                <label class="text-sm text-gray-500">{{$t('admin.views.settings.plot_width_max')}}</label>
                <input
                  type="number"
                  style="width: 80px"
                  v-model="form.plot_width_max"
                  required
                />
              </div>
              <div class="mb-2">{{$t('admin.views.settings.plot_diapozon_width')}}</div>
            </div>
            <div class="line-default mt-2">
              <div>{{$t('admin.views.settings.plot_default_size')}}</div>
              <div class="plot-size-line flex items-end gap-3">
                <div class="form-group">
                  <label class="text-sm text-gray-500">{{$t('admin.views.settings.plot_length_default')}}</label>
                  <input
                    type="number"
                    style="width: 80px"
                    v-model="form.plot_length_default"
                    required
                  />
                </div>
                <div class="mb-4 border-b w-10 border-black"></div>
                <div class="form-group">
                  <label class="text-sm text-gray-500">{{$t('admin.views.settings.plot_width_default')}}</label>
                  <input
                    type="number"
                    style="width: 80px"
                    v-model="form.plot_width_default"
                    required
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
        <div v-if="tab == 'user-setting'">
          <h2 class="text-lg font-semibold mb-4">{{$t('admin.views.settings.tab_user')}}</h2>
          <div class="form text-sm">
            <div class="form-group">
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.show_contacts_block = !form.show_contacts_block
                  "
                >
                  <span class="check"><i
                      v-if="form.show_contacts_block == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.show_contacts_block')}}</span>
                </button>
              </div>
            </div>

            <div class="form-group">
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.show_phone_for_client = !form.show_phone_for_client
                  "
                >
                  <span class="check"><i
                      v-if="form.show_phone_for_client == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.show_phone')}}</span>
                </button>
              </div>
            </div>
            <div class="form-group w-3/5">
              <label for="phone_for_client">{{$t('admin.views.settings.label_phone')}}:</label>
              <div>
                <input
                  id="phone_for_client"
                  type="text"
                  v-model="form.phone_for_client"
                />
              </div>
              <ValidationErrors :errors="errorsFor('phone_for_client')" />
            </div>

            <div class="form-group">
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.show_contact_company = !form.show_contact_company
                  "
                >
                  <span class="check"><i
                      v-if="form.show_contact_company == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.show_contact_company')}}</span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.show_contact_inn = !form.show_contact_inn
                  "
                >
                  <span class="check"><i
                      v-if="form.show_contact_inn == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.show_contact_inn')}}</span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.show_contact_address = !form.show_contact_address
                  "
                >
                  <span class="check"><i
                      v-if="form.show_contact_address == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.show_contact_address')}}</span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.show_contact_phone = !form.show_contact_phone
                  "
                >
                  <span class="check"><i
                      v-if="form.show_contact_phone == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.show_contact_phone')}}</span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.show_contact_phone_city = !form.show_contact_phone_city
                  "
                >
                  <span class="check"><i
                      v-if="form.show_contact_phone_city == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.show_contact_phone_city')}}</span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.show_contact_email = !form.show_contact_email
                  "
                >
                  <span class="check"><i
                      v-if="form.show_contact_email == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.show_contact_email')}}</span>
                </button>
              </div>
            </div>

            <div class="form-group w-3/5">
              <label
                for="link_to_site_for_client"
                class="flex items-center"
                style="display: flex"
              ><span>{{$t('admin.views.settings.link_for_main_site')}}:</span>
                <hint :textHint="$t('admin.views.settings.link_for_main_site_hint')" />
              </label>
              <div class="flex gap-3">
                <input
                  id="link_to_site_for_client"
                  type="text"
                  v-model="form.link_to_site_for_client"
                />
                <select
                  v-model="form.target_link_to_site_for_client"
                  style="width: auto"
                >
                  <option value="_blank">_blank</option>
                  <option value="_self">_self</option>
                </select>
              </div>
              <ValidationErrors :errors="errorsFor('link_to_site_for_client')" />
            </div>
            <div class="form-group">
              <div>
                <button
                  class="flex focus:outline-none"
                  @click="
                    form.show_link_to_video_instruction =
                      !form.show_link_to_video_instruction
                  "
                >
                  <span class="check"><i
                      v-if="form.show_link_to_video_instruction == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm text-left">{{$t('admin.views.settings.show_video_instruction')}}</span>
                </button>
              </div>
            </div>

            <div class="form-group flex gap-4">
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.show_performance_regim = !form.show_performance_regim
                  "
                >
                  <span class="check"><i
                      v-if="form.show_performance_regim == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.show_performance_regim')}}</span>
                </button>
              </div>
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.default_active_performance_regim = !form.default_active_performance_regim
                  "
                >
                  <span class="check"><i
                      v-if="form.default_active_performance_regim == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.default_active_performance_regim')}}</span>
                </button>
              </div>
            </div>

            <div class="form-group flex gap-4">
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.show_change_env = !form.show_change_env
                  "
                >
                  <span class="check"><i
                      v-if="form.show_change_env == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.show_change_env')}}</span>
                </button>
              </div>
              <div>
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.default_no_env = !form.default_no_env
                  "
                >
                  <span class="check"><i
                      v-if="form.default_no_env == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm">{{$t('admin.views.settings.default_no_env')}}</span>
                </button>
              </div>
            </div>

            <div class="form-group">
              <div>
                <button
                  class="rounded flex justify-center focus:outline-none"
                  @click="form.show_info_pages = !form.show_info_pages"
                >
                  <span class="check"><i
                      v-if="form.show_info_pages == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm flex gap-3 text-left"><span>{{$t('admin.views.settings.show_page_block')}}</span>
                    <hint :textHint="$t('admin.views.settings.info_pages')" />
                  </span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <div>
                <button
                  class="rounded flex justify-center focus:outline-none"
                  @click="
                    form.show_button_pdf_order = !form.show_button_pdf_order
                  "
                >
                  <span class="check"><i
                      v-if="form.show_button_pdf_order == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm text-left">{{$t('admin.views.settings.show_button_download_pdf_order')}}</span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <div>
                <button
                  class="rounded flex justify-center focus:outline-none"
                  @click="
                    form.show_informer = !form.show_informer
                  "
                >
                  <span class="check"><i
                      v-if="form.show_informer == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm text-left">{{$t('admin.views.settings.show_informer')}}</span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <div>
                <button
                  class="rounded flex justify-center focus:outline-none"
                  @click="
                    form.show_delete_button_client_confirm_order = !form.show_delete_button_client_confirm_order
                  "
                >
                  <span class="check"><i
                      v-if="form.show_delete_button_client_confirm_order == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm text-left">{{$t('admin.views.settings.show_delete_button_client_confirm_order')}}</span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <h3 class="font-semibold mb-2 text-md">Кол-во выводимых элементов в списках каталогов пользовательской части до подгрузки</h3>
              <div class="flex gap-4 items-center mb-4">
                <div class="w-20">
                  <input
                    id="size_3d_environment"
                    class="w-20"
                    min="12"
                    type="number"
                    v-model="form.count_elements_configurator"
                  />
                </div>

                <label
                  for="size_3d_environment"
                  class="text-gray-400 text-xs"
                >кол-во элементов в каталогах конфигуратора, шт.</label>
              </div>
              <div class="flex gap-4 items-center">
                <div class="w-20">
                  <input
                    id="size_3d_environment"
                    class="w-20"
                    min="12"
                    type="number"
                    v-model="form.count_elements_engravings"
                  />
                </div>

                <label
                  for="size_3d_environment"
                  class="text-gray-400 text-xs"
                >кол-во элементов в каталогах оформления, шт.</label>
              </div>
            </div>

            <div class="form-group">
              <h3 class="font-semibold mb-2 text-md">{{$t('admin.views.settings.size_env_3d')}}</h3>
              <div class="flex gap-4 items-center">
                <div class="w-20">
                  <input
                    id="size_3d_environment"
                    class="w-20"
                    min="5"
                    type="number"
                    v-model="form.size_3d_environment"
                  />
                </div>

                <label
                  for="size_3d_environment"
                  class="text-gray-400 text-xs"
                >{{$t('admin.views.settings.size_env_limits')}}</label>
              </div>
            </div>
            <div class="form-group">
              <h3 class="font-semibold mb-2 text-md">{{$t('admin.views.settings.textures_env')}} </h3>
              <div class="flex items-center gap-2">
                <input
                  type="radio"
                  name="3d_envTexture"
                  value="0"
                  v-model="form.env_3d_texture"
                  id="3d_env_texture_0"
                />
                <label for="3d_env_texture_0">{{$t('admin.views.settings.fon_default')}}</label>
              </div>
              <div class="flex items-center gap-2">
                <input
                  type="radio"
                  name="3d_envTexture"
                  value="1"
                  v-model="form.env_3d_texture"
                  id="3d_env_texture_1"
                />
                <label for="3d_env_texture_1">{{$t('admin.views.settings.fon_no')}}</label>
              </div>
              <div class="flex items-center gap-2">
                <input
                  type="radio"
                  name="3d_envTexture"
                  value="2"
                  v-model="form.env_3d_texture"
                  id="3d_env_texture_2"
                />
                <label for="3d_env_texture_2">{{$t('admin.views.settings.fon_my')}}</label>
              </div>

              <div
                class="env__faces_images"
                v-if="form.env_3d_texture==2"
              >
                <h4 class="font-semibold mb-3">{{$t('admin.views.settings.required_fields')}}</h4>
                <div class="env__faces">
                  <div class="env__face">
                    <div class="env__face-index">1.</div>
                    <div class="env__face-icon"><img
                        src="/images/icons/icons-env3d/px.svg"
                        alt=""
                      ></div>
                    <div class="env__face-label">PX:</div>
                    <div class="env__face-input">

                      <FileInputBlockNoPreview
                        :selectedImageFileNameDefault="$t('admin.views.settings.image_not_selected')"
                        :alreadyLoadText="$t('admin.views.settings.image_selected')"
                        :loadedFile="settingImages.env_px||setSettingImages.env_px"
                        @selected-file="(file) => selectFile(file, 'env_px')"
                        @remove-file="removeFile('env_px')"
                      >
                      </FileInputBlockNoPreview>
                    </div>
                  </div>

                  <div class="env__face">
                    <div class="env__face-index">2.</div>
                    <div class="env__face-icon"><img
                        src="/images/icons/icons-env3d/py.svg"
                        alt=""
                      ></div>
                    <div class="env__face-label">PY:</div>
                    <div class="env__face-input">

                      <FileInputBlockNoPreview
                        :selectedImageFileNameDefault="$t('admin.views.settings.image_not_selected')"
                        :alreadyLoadText="$t('admin.views.settings.image_selected')"
                        :loadedFile="settingImages.env_py||setSettingImages.env_py"
                        @selected-file="(file) => selectFile(file, 'env_py')"
                        @remove-file="removeFile('env_py')"
                      >
                      </FileInputBlockNoPreview>
                    </div>
                  </div>

                  <div class="env__face">
                    <div class="env__face-index">3.</div>
                    <div class="env__face-icon"><img
                        src="/images/icons/icons-env3d/pz.svg"
                        alt=""
                      ></div>
                    <div class="env__face-label">PZ:</div>
                    <div class="env__face-input">

                      <FileInputBlockNoPreview
                        :selectedImageFileNameDefault="$t('admin.views.settings.image_not_selected')"
                        :alreadyLoadText="$t('admin.views.settings.image_selected')"
                        :loadedFile="settingImages.env_pz||setSettingImages.env_pz"
                        @selected-file="(file) => selectFile(file, 'env_pz')"
                        @remove-file="removeFile('env_pz')"
                      >
                      </FileInputBlockNoPreview>
                    </div>
                  </div>
                  <div class="env__face">
                    <div class="env__face-index">4.</div>
                    <div class="env__face-icon"><img
                        src="/images/icons/icons-env3d/nx.svg"
                        alt=""
                      ></div>
                    <div class="env__face-label">NX:</div>
                    <div class="env__face-input">

                      <FileInputBlockNoPreview
                        :selectedImageFileNameDefault="$t('admin.views.settings.image_not_selected')"
                        :alreadyLoadText="$t('admin.views.settings.image_selected')"
                        :loadedFile="settingImages.env_nx||setSettingImages.env_nx"
                        @selected-file="(file) => selectFile(file, 'env_nx')"
                        @remove-file="removeFile('env_nx')"
                      >
                      </FileInputBlockNoPreview>
                    </div>
                  </div>
                  <div class="env__face">
                    <div class="env__face-index">5.</div>
                    <div class="env__face-icon"><img
                        src="/images/icons/icons-env3d/ny.svg"
                        alt=""
                      ></div>
                    <div class="env__face-label">NY:</div>
                    <div class="env__face-input">

                      <FileInputBlockNoPreview
                        :selectedImageFileNameDefault="$t('admin.views.settings.image_not_selected')"
                        :alreadyLoadText="$t('admin.views.settings.image_selected')"
                        :loadedFile="settingImages.env_ny||setSettingImages.env_ny"
                        @selected-file="(file) => selectFile(file, 'env_ny')"
                        @remove-file="removeFile('env_ny')"
                      >
                      </FileInputBlockNoPreview>
                    </div>
                  </div>

                  <div class="env__face">
                    <div class="env__face-index">6.</div>
                    <div class="env__face-icon"><img
                        src="/images/icons/icons-env3d/nz.svg"
                        alt=""
                      ></div>
                    <div class="env__face-label">NZ:</div>
                    <div class="env__face-input">

                      <FileInputBlockNoPreview
                        :selectedImageFileNameDefault="$t('admin.views.settings.image_not_selected')"
                        :alreadyLoadText="$t('admin.views.settings.image_selected')"
                        :loadedFile="settingImages.env_nz||setSettingImages.env_nz"
                        @selected-file="(file) => selectFile(file, 'env_nz')"
                        @remove-file="removeFile('env_nz')"
                      >
                      </FileInputBlockNoPreview>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <h3 class="font-semibold mb-2 text-md">{{$t('admin.views.settings.plot_texture')}}</h3>
              <div class="flex items-center gap-2">
                <input
                  type="radio"
                  name="plotTexture"
                  value="0"
                  v-model="form.plot_texture"
                  id="plot_texture_0"
                />
                <label for="plot_texture_0">{{$t('admin.views.settings.texture_default')}}</label>
              </div>
              <div class="flex items-center gap-2">
                <input
                  type="radio"
                  name="plotTexture"
                  value="1"
                  v-model="form.plot_texture"
                  id="plot_texture_1"
                />
                <label for="plot_texture_1">{{$t('admin.views.settings.texture_no')}}</label>
              </div>
              <div class="flex items-center gap-2">
                <input
                  type="radio"
                  name="plotTexture"
                  value="2"
                  v-model="form.plot_texture"
                  id="plot_texture_2"
                />
                <label for="plot_texture_2">{{$t('admin.views.settings.texture_my')}}</label>
              </div>

              <div
                class="env__faces_images"
                v-if="form.plot_texture==2"
              >
                <div class="env__faces">
                  <div class="env__face">

                    <div class="env__face-input">

                      <FileInputBlockNoPreview
                        :selectedImageFileNameDefault="$t('admin.views.settings.image_not_selected')"
                        :alreadyLoadText="$t('admin.views.settings.image_selected')"
                        :loadedFile="settingImages.plot_texture||setSettingImages.plot_texture"
                        @selected-file="(file) => selectFile(file, 'plot_texture')"
                        @remove-file="removeFile('plot_texture')"
                      >
                      </FileInputBlockNoPreview>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <div class="form-group">
              <h3 class="font-semibold mb-2 text-md">{{$t('admin.views.settings.filler_cvetnik_texture')}}</h3>
              <div class="flex items-center gap-2">
                <input
                  type="radio"
                  name="fillerСvetnikЕexture"
                  value="0"
                  v-model="form.filler_cvetnik_texture"
                  id="filler_cvetnik_texture_0"
                />
                <label for="filler_cvetnik_texture_0">{{$t('admin.views.settings.texture_default')}}</label>
              </div>
              <div class="flex items-center gap-2">
                <input
                  type="radio"
                  name="fillerСvetnikЕexture"
                  value="1"
                  v-model="form.filler_cvetnik_texture"
                  id="filler_cvetnik_texture_1"
                />
                <label for="filler_cvetnik_texture_1">{{$t('admin.views.settings.texture_no')}}</label>
              </div>
              <div class="flex items-center gap-2">
                <input
                  type="radio"
                  name="fillerСvetnikЕexture"
                  value="2"
                  v-model="form.filler_cvetnik_texture"
                  id="filler_cvetnik_texture_2"
                />
                <label for="filler_cvetnik_texture_2">{{$t('admin.views.settings.texture_my')}}</label>
              </div>

              <div
                class="env__faces_images"
                v-if="form.filler_cvetnik_texture==2"
              >
                <div class="env__faces">
                  <div class="env__face">

                    <div class="env__face-input">

                      <FileInputBlockNoPreview
                        :selectedImageFileNameDefault="$t('admin.views.settings.image_not_selected')"
                        :alreadyLoadText="$t('admin.views.settings.image_selected')"
                        :loadedFile="settingImages.filler_cvetnik_texture||setSettingImages.filler_cvetnik_texture"
                        @selected-file="(file) => selectFile(file, 'filler_cvetnik_texture')"
                        @remove-file="removeFile('filler_cvetnik_texture')"
                      >
                      </FileInputBlockNoPreview>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <div class="form-group">

              <h3 class="font-semibold mb-2 text-md">{{$t('admin.views.settings.rotate_angle')}}</h3>
              <div class="w-20">
                <select
                  v-model="form.rotate_angle"
                  id="system-rotate-angle"
                >
                  <option
                    :value="angle"
                    v-for="angle in angles"
                    :key="`angle-${angle}`"
                  >
                    {{ angle }}
                  </option>
                </select>
              </div>
            </div>

          </div>
        </div>
        <div v-if="
                tab=='display'">
          <h2 class="
                text-lg
                font-semibold
                mb-4">{{$t('admin.views.settings.fast_access')}}</h2>
          <div class="form text-sm">
            <div class="grid grid-cols-2 items-start gap-4">
              <div
                class=""
                v-for="fab in fastAccessButtons"
                :key="`fab-${fab.code}`"
              >
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.fast_access.some((fa) => fa.code === fab.code)
                      ? removeFastAccess(fab.code)
                      : addFastAccess(fab)
                  "
                >
                  <span class="check"><i
                      v-if="form.fast_access.some((fa) => fa.code === fab.code)"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm"> {{ fab.title }} </span>
                </button>
              </div>
            </div>
          </div>

          <p class="mt-8 mb-8">
            {{$t('admin.views.settings.info_fast_access')}}
          </p>

          <h2 class="text-lg font-semibold mb-4"> {{$t('admin.views.settings.elements_catalog')}}</h2>
          <div class="form text-sm">
            <div class="grid grid-cols-2 items-start gap-4">
              <div
                class=""
                v-for="elCat in elementsCatalog"
                :key="`elCat-${elCat.code}`"
              >
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.elements_catalog.some((el) => el.code === elCat.code)
                      ? removeClientBlock('elements_catalog', elCat.code)
                      : addClientBlock('elements_catalog', elCat)
                  "
                >
                  <span class="check"><i
                      v-if="
                        form.elements_catalog.some(
                          (el) => el.code === elCat.code
                        )
                      "
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm"> {{ elCat.title }} </span>
                </button>
              </div>
            </div>
          </div>

          <div class="mt-8 mb-4">
            <button
              class="rounded flex items-center focus:outline-none gap-2"
              @click="form.use_landscaping = !form.use_landscaping"
            >
              <span class="check"><i
                  v-if="form.use_landscaping == '1'"
                  class="fas fa-check text-red-500"
                ></i></span>
              <span class="text-lg font-semibold">{{$t('admin.views.settings.landscaping')}}</span>
            </button>
          </div>
          <div class="form text-sm">
            <div class="grid grid-cols-2 items-start gap-4">
              <div
                class=""
                v-for="lsc in landscapingsCatalog"
                :key="`lsc-${lsc.code}`"
              >
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.landscapings_catalog.some((el) => el.code === lsc.code)
                      ? removeClientBlock('landscapings_catalog', lsc.code)
                      : addClientBlock('landscapings_catalog', lsc)
                  "
                >
                  <span class="check"><i
                      v-if="
                        form.landscapings_catalog.some(
                          (el) => el.code === lsc.code
                        )
                      "
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm"> {{ lsc.title }} </span>
                </button>
              </div>
            </div>
          </div>

          <div class="mt-8 mb-4">
            <button
              class="rounded flex items-center focus:outline-none gap-2"
              @click="form.use_engraving = !form.use_engraving"
            >
              <span class="check"><i
                  v-if="form.use_engraving == '1'"
                  class="fas fa-check text-red-500"
                ></i></span>
              <span class="text-lg font-semibold">{{$t('admin.views.settings.design')}}</span>
            </button>
          </div>

          <div class="form text-sm">
            <div class="grid grid-cols-2 items-start gap-4">
              <div
                class=""
                v-for="egc in engravingsCatalog"
                :key="`egc-${egc.code}`"
              >
                <button
                  class="
                    rounded
                    flex
                    justify-center
                    items-center
                    focus:outline-none
                  "
                  @click="
                    form.engravings_catalog.some((el) => el.code === egc.code)
                      ? removeClientBlock('engravings_catalog', egc.code)
                      : addClientBlock('engravings_catalog', egc)
                  "
                >
                  <span class="check"><i
                      v-if="
                        form.engravings_catalog.some(
                          (el) => el.code === egc.code
                        )
                      "
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="ml-2 text-sm"> {{ egc.title }} </span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="tab == 'commerce'">
          <h2 class="text-lg font-semibold mb-4">{{$t('admin.views.settings.commerce')}}</h2>
          <div class="form text-sm">
            <h3 class="text-md font-semibold mb-4">{{$t('admin.views.settings.price_calc')}}</h3>
            <div class="form-group flex items-baseline gap-2">
              <input
                type="radio"
                name="with_calc_price"
                value="0"
                v-model="form.with_calc_price"
                id="constructor_without_calc_price"
              />
              <label for="constructor_without_calc_price">{{$t('admin.views.settings.work_without_price')}}</label>
            </div>
            <div class="form-group flex items-baseline gap-2">
              <input
                type="radio"
                name="with_calc_price"
                v-model="form.with_calc_price"
                value="1"
                id="constructor_with_calc_price"
              />
              <label for="constructor_with_calc_price">{{$t('admin.views.settings.work_with_price')}}</label>
            </div>
            <div class="form-group flex items-center pl-6 gap-2">
              <label
                class="text-md"
                for="currency"
              >{{$t('admin.views.settings.currency')}}</label>
              <select
                v-model="form.currency"
                style="width: auto"
              >
                <option
                  :value="c.code"
                  v-for="c in currencies"
                  :key="`currency-${c.code}`"
                >
                  {{ c.name }}
                </option>
              </select>
            </div>

            <div class="form-group marketing_settings">
              <div class="marketing_settings__buttons">
                <button
                  class="rounded flex items-center focus:outline-none gap-2"
                  @click="form.informer_approximate_price = !form.informer_approximate_price"
                >
                  <span class="check"><i
                      v-if="form.informer_approximate_price == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="text-sm">{{$t('admin.views.settings.informer_approximate_price')}}</span>
                </button>
              </div>
              <div class="marketing_settings__textarea">
                <textarea v-model="form.informer_approximate_price_value"></textarea>
              </div>
            </div>

            <h3 class="text-md font-semibold mb-4 mt-4">{{$t('admin.views.settings.cost_price')}}</h3>

            <div class="form-group">
              <div>
                <button
                  class="rounded flex items-center focus:outline-none gap-2"
                  @click="form.calc_cost_price = !form.calc_cost_price"
                >
                  <span class="check"><i
                      v-if="form.calc_cost_price == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="text-sm">{{$t('admin.views.settings.calc_cost_price')}}</span>
                </button>
              </div>
            </div>

            <h3 class="text-md font-semibold mb-4 mt-4">{{$t('admin.views.settings.prepaid')}}</h3>

            <div class="form-group flex items-baseline gap-2">
              <input
                type="radio"
                name="prepaid"
                v-model="form.prepaid"
                value="0"
                id="prepaid_0"
              />
              <label for="prepaid_0">{{$t('admin.views.settings.prepaid_0')}}</label>
            </div>
            <div></div>
            <div class="form-group flex items-baseline gap-2">
              <input
                type="radio"
                name="prepaid"
                v-model="form.prepaid"
                value="1"
                id="prepaid_1"
              />
              <label for="prepaid_1">{{$t('admin.views.settings.prepaid_1')}}</label>
            </div>
            <div
              v-if="form.prepaid=='1'"
              class="ml-5"
            >
              <div class="form-group flex items-baseline gap-2">
                <input
                  type="radio"
                  name="prepaid_type"
                  v-model="form.prepaid_type"
                  value="fix"
                  id="prepaid_type_fix"
                />
                <label for="prepaid_type_fix">{{$t('admin.views.settings.prepaid_type_fix')}}</label>
              </div>
              <div
                v-if="form.prepaid_type=='fix'"
                class="ml-4 mb-2"
              >
                <div class="form-group flex items-baseline gap-2">
                  <input
                    type="text"
                    style="width: 80px"
                    v-model="form.prepaid_type_fix_value"
                  />
                  <span class="text-lg">&#8381;</span>
                </div>
              </div>
              <div></div>
              <div class="form-group flex items-baseline gap-2">
                <input
                  type="radio"
                  name="prepaid_type"
                  v-model="form.prepaid_type"
                  value="percent_total_price"
                  id="prepaid_type_percent_total_price"
                />
                <label for="prepaid_type_percent_total_price">{{$t('admin.views.settings.prepaid_type_percent_total_price')}}</label>
              </div>
              <div
                v-if="form.prepaid_type=='percent_total_price'"
                class="ml-4 mb-2"
              >
                <div class="form-group flex items-baseline gap-2">
                  <input
                    type="text"
                    style="width: 80px"
                    v-model="form.prepaid_type_percent_total_price_value"
                  />
                  <span class="text-lg">%</span>
                </div>
              </div>
              <div></div>
              <div class="form-group flex items-baseline gap-2">
                <input
                  type="radio"
                  name="prepaid_type"
                  v-model="form.prepaid_type"
                  value="percent_total_cost"
                  id="prepaid_type_percent_total_cost"
                />
                <label for="prepaid_type_percent_total_cost">{{$t('admin.views.settings.prepaid_type_percent_total_cost')}}</label>
              </div>
              <div
                v-if="form.prepaid_type=='percent_total_cost'"
                class="ml-4 mb-2"
              >
                <div class="form-group flex items-baseline gap-2">
                  <input
                    type="text"
                    style="width: 80px"
                    v-model="form.prepaid_type_percent_total_cost_value"
                  />
                  <span class="text-lg">%</span>
                </div>
              </div>
            </div>

            <h3 class="text-md font-semibold mb-4 mt-4">{{$t('admin.views.settings.marketing')}}</h3>

            <div class="form-group">
              <div>
                <button
                  class="rounded flex items-center focus:outline-none gap-2"
                  @click="form.winter_storage = !form.winter_storage"
                >
                  <span class="check"><i
                      v-if="form.winter_storage == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="text-sm">{{$t('admin.views.settings.winter_storage')}}</span>
                </button>
              </div>
            </div>

            <div class="form-group marketing_settings">
              <div class="marketing_settings__buttons">
                <button
                  class="rounded flex items-center focus:outline-none gap-2"
                  @click="form.show_conditions_storage = !form.show_conditions_storage"
                >
                  <span class="check"><i
                      v-if="form.show_conditions_storage == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="text-sm">{{$t('admin.views.settings.show_conditions_storage')}}</span>
                </button>
                <button
                  class="rounded flex items-center focus:outline-none gap-2"
                  @click="form.show_conditions_storage_in_pdf = !form.show_conditions_storage_in_pdf"
                >
                  <span class="check"><i
                      v-if="form.show_conditions_storage_in_pdf == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="text-sm">{{$t('admin.views.settings.show_in_pdf')}}</span>
                </button>
              </div>
              <div class="marketing_settings__textarea">
                <textarea v-model="form.conditions_storage_value"></textarea>
              </div>
            </div>

            <div class="form-group flex items-baseline gap-2">
              <input
                type="radio"
                name="sale"
                v-model="form.sale"
                value="no"
                id="constructor_sale_not"
              />
              <label for="constructor_sale_not">{{$t('admin.views.settings.no_sale')}}</label>
            </div>
            <div class="form-group flex items-baseline gap-2">
              <input
                type="radio"
                name="sale"
                v-model="form.sale"
                value="individual"
                id="constructor_sale_individual"
              />
              <label for="constructor_sale_individual">{{$t('admin.views.settings.individual_sale')}}</label>
            </div>
            <div class="form-group flex items-baseline gap-2">
              <input
                type="radio"
                name="sale"
                v-model="form.sale"
                value="sale_with_order"
                id="constructor_sale_with_order"
              />
              <label for="constructor_sale_with_order">{{$t('admin.views.settings.sale_for_order')}}</label>
              <input
                type="text"
                style="width: 50px"
                v-model="form.constructor_sale_with_order_percent_value"
              />
              <span class="text-md">%</span>
            </div>
            <div class="form-group flex items-baseline gap-2">
              <input
                type="radio"
                name="sale"
                v-model="form.sale"
                value="sale_with_order_from"
                id="constructor_sale_with_order_from"
              />
              <label for="constructor_sale_with_order_from">{{$t('admin.views.settings.sale')}}</label>
              <input
                type="text"
                style="width: 50px"
                v-model="form.constructor_sale_with_order_percent_from_value"
              />
              <span class="text-md">%</span>
              <span> {{$t('admin.views.settings.sale_in_order_ot')}} </span>
              <input
                type="text"
                style="width: 50px"
                v-model="form.constructor_sale_with_order_total_from_value"
              />
              <span class="text-md">{{$t('main.currency')}}</span>
            </div>

            <div class="form-group flex items-baseline gap-2">
              <input
                type="radio"
                name="sale"
                v-model="form.sale"
                value="sale_with_order_season"
                id="constructor_sale_with_order_season"
              />
              <label for="constructor_sale_with_order_season">{{$t('admin.views.settings.sale_season')}}</label>
              <input
                type="text"
                style="width: 50px"
                v-model="form.constructor_sale_with_order_percent_season_value"
              />
              <span class="text-md">%</span>
              <span> {{$t('admin.views.settings.sale_s')}} </span>
              <input
                type="date"
                style="width: auto"
                v-model="
                  form.constructor_sale_with_order_percent_season_value_start_date
                "
              />
              <span> {{$t('admin.views.settings.sale_po')}} </span>
              <input
                type="date"
                style="width: auto"
                v-model="
                  form.constructor_sale_with_order_percent_season_value_finish_date
                "
              />

            </div>

            <div class="form-group">
              <div>
                <button
                  class="rounded flex items-center focus:outline-none gap-2"
                  @click="form.shipping = !form.shipping"
                >
                  <span class="check"><i
                      v-if="form.shipping == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="text-sm">{{$t('admin.views.settings.delivery')}}</span>
                </button>
              </div>
            </div>
            <div class="form-group marketing_settings">
              <div class="marketing_settings__buttons">
                <button
                  class="rounded flex items-center focus:outline-none gap-2"
                  @click="form.show_conditions_shipping = !form.show_conditions_shipping"
                >
                  <span class="check"><i
                      v-if="form.show_conditions_shipping == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="text-sm">{{$t('admin.views.settings.show_conditions_shipping')}}</span>
                </button>
                <button
                  class="rounded flex items-center focus:outline-none gap-2"
                  @click="form.show_conditions_shipping_in_pdf = !form.show_conditions_shipping_in_pdf"
                >
                  <span class="check"><i
                      v-if="form.show_conditions_shipping_in_pdf == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="text-sm">{{$t('admin.views.settings.show_in_pdf')}}</span>
                </button>
              </div>
              <div class="marketing_settings__textarea">
                <textarea v-model="form.conditions_shipping_value"></textarea>
              </div>
            </div>
            <div class="form-group marketing_settings">
              <div class="marketing_settings__buttons">
                <button
                  class="rounded flex items-center focus:outline-none gap-2"
                  @click="form.show_field_shipping_info = !form.show_field_shipping_info"
                >
                  <span class="check"><i
                      v-if="form.show_field_shipping_info == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="text-sm">{{$t('admin.views.settings.show_field_shipping_info')}}</span>
                </button>
                <button
                  class="rounded flex items-center focus:outline-none gap-2"
                  @click="form.field_shipping_info_required = !form.field_shipping_info_required"
                >
                  <span class="check"><i
                      v-if="form.field_shipping_info_required == '1'"
                      class="fas fa-check text-red-500"
                    ></i></span>
                  <span class="text-sm">{{$t('admin.views.settings.field_required')}}</span>
                </button>
              </div>
              <div class="marketing_settings__textarea">
                <label for="label_field_shipping_info">{{$t('admin.views.settings.label_field_shipping_info')}}</label>
                <input
                  id="label_field_shipping_info"
                  type="text"
                  v-model="form.label_field_shipping_info"
                >
              </div>
            </div>
            <div class="form-group fields_form_confirm_order">
              <div class="fields_form_confirm_order__title">{{$t('admin.views.settings.fields_form_confirm_order_title')}}</div>
              <div class="fields_form_confirm_order__list">
                <div class="fields_form_confirm_order__item">
                  <div class="fields_form_confirm_order__show">
                    <button
                      class="rounded flex items-center focus:outline-none gap-2"
                      @click="form.show_form_confirm_order_name = !form.show_form_confirm_order_name"
                    >
                      <span class="check"><i
                          v-if="form.show_form_confirm_order_name == '1'"
                          class="fas fa-check text-red-500"
                        ></i></span>
                      <span class="text-sm">{{$t('admin.views.settings.fields_form_confirm_order_name')}}</span>
                    </button>
                  </div>
                  <div class="fields_form_confirm_order__req">
                    <button
                      class="rounded flex items-center focus:outline-none gap-2"
                      @click="form.required_form_confirm_order_name = !form.required_form_confirm_order_name"
                    >
                      <span class="check"><i
                          v-if="form.required_form_confirm_order_name == '1'"
                          class="fas fa-check text-red-500"
                        ></i></span>
                      <span class="text-sm">{{$t('admin.views.settings.field_required')}}</span>
                    </button>
                  </div>
                </div>
                <div class="fields_form_confirm_order__item">
                  <div class="fields_form_confirm_order__show">
                    <button
                      class="rounded flex items-center focus:outline-none gap-2"
                      @click="form.show_form_confirm_order_phone = !form.show_form_confirm_order_phone"
                    >
                      <span class="check"><i
                          v-if="form.show_form_confirm_order_phone == '1'"
                          class="fas fa-check text-red-500"
                        ></i></span>
                      <span class="text-sm">{{$t('admin.views.settings.fields_form_confirm_order_phone')}}</span>
                    </button>
                  </div>
                  <div class="fields_form_confirm_order__req">
                    <button
                      class="rounded flex items-center focus:outline-none gap-2"
                      @click="form.required_form_confirm_order_phone = !form.required_form_confirm_order_phone"
                    >
                      <span class="check"><i
                          v-if="form.required_form_confirm_order_phone == '1'"
                          class="fas fa-check text-red-500"
                        ></i></span>
                      <span class="text-sm">{{$t('admin.views.settings.field_required')}}</span>
                    </button>
                  </div>
                </div>
                <div class="fields_form_confirm_order__item">
                  <div class="fields_form_confirm_order__show">
                    <button
                      class="rounded flex items-center focus:outline-none gap-2"
                      @click="form.show_form_confirm_order_email = !form.show_form_confirm_order_email"
                    >
                      <span class="check"><i
                          v-if="form.show_form_confirm_order_email == '1'"
                          class="fas fa-check text-red-500"
                        ></i></span>
                      <span class="text-sm">{{$t('admin.views.settings.fields_form_confirm_order_email')}}</span>
                    </button>
                  </div>
                  <div class="fields_form_confirm_order__req">
                    <button
                      class="rounded flex items-center focus:outline-none gap-2"
                      @click="form.required_form_confirm_order_email = !form.required_form_confirm_order_email"
                    >
                      <span class="check"><i
                          v-if="form.required_form_confirm_order_email == '1'"
                          class="fas fa-check text-red-500"
                        ></i></span>
                      <span class="text-sm">{{$t('admin.views.settings.field_required')}}</span>
                    </button>
                  </div>
                </div>
                <div class="fields_form_confirm_order__item">
                  <div class="fields_form_confirm_order__show">
                    <button
                      class="rounded flex items-center focus:outline-none gap-2"
                      @click="form.show_form_confirm_order_comment = !form.show_form_confirm_order_comment"
                    >
                      <span class="check"><i
                          v-if="form.show_form_confirm_order_comment == '1'"
                          class="fas fa-check text-red-500"
                        ></i></span>
                      <span class="text-sm">{{$t('admin.views.settings.fields_form_confirm_order_comment')}}</span>
                    </button>
                  </div>
                  <div class="fields_form_confirm_order__req">
                    <button
                      class="rounded flex items-center focus:outline-none gap-2"
                      @click="form.required_form_confirm_order_comment = !form.required_form_confirm_order_comment"
                    >
                      <span class="check"><i
                          v-if="form.required_form_confirm_order_comment == '1'"
                          class="fas fa-check text-red-500"
                        ></i></span>
                      <span class="text-sm">{{$t('admin.views.settings.field_required')}}</span>
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
        <div v-if="tab == 'company'">
          <h2 class="text-lg font-semibold mb-4">{{$t('admin.views.settings.tab_company')}}</h2>
          <div class="form w-1/3 text-sm">
            <div class="form-group">
              <label for="company_name">{{$t('admin.views.settings.company_name')}}:</label>
              <div>
                <input
                  id="company_name"
                  type="text"
                  v-model="form.company_name"
                />
              </div>
              <ValidationErrors :errors="errorsFor('company_name')" />
            </div>
            <div class="form-group">
              <label for="company_inn">{{$t('admin.views.settings.company_inn')}}:</label>
              <div>
                <input
                  id="company_inn"
                  type="text"
                  v-model="form.company_inn"
                />
              </div>
              <ValidationErrors :errors="errorsFor('company_inn')" />
            </div>
            <div class="form-group">
              <label for="company_address">{{$t('admin.views.settings.company_address')}}:</label>
              <div>
                <input
                  id="company_address"
                  type="text"
                  v-model="form.company_address"
                />
              </div>
              <ValidationErrors :errors="errorsFor('company_address')" />
            </div>
            <div class="form-group">
              <label for="company_phone">{{$t('admin.views.settings.company_phone')}}:</label>
              <div>
                <input
                  id="company_phone"
                  type="text"
                  v-model="form.company_phone"
                />
              </div>
              <ValidationErrors :errors="errorsFor('company_phone')" />
            </div>
            <div class="form-group">
              <label for="company_phone_cities">{{$t('admin.views.settings.company_phone_city')}}:</label>
              <div>
                <input
                  id="company_phone_cities"
                  type="text"
                  v-model="form.company_phone_cities"
                />
              </div>
              <ValidationErrors :errors="errorsFor('company_phone_cities')" />
            </div>
            <div class="form-group">
              <label for="company_email">E-mail:</label>
              <div>
                <input
                  id="company_email"
                  type="text"
                  v-model="form.company_email"
                />
              </div>
              <ValidationErrors :errors="errorsFor('company_email')" />
            </div>
            <div class="form-group">
              <label for="company_site">{{$t('admin.views.settings.company_site')}}:</label>
              <div>
                <input
                  id="company_site"
                  type="text"
                  v-model="form.company_site"
                />
              </div>
              <ValidationErrors :errors="errorsFor('company_site')" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import validationErrors from "../../mixins/validationErrors";
import Hint from "../../shared/components/Hint.vue";
export default {
  components: { Hint },
  mixins: [validationErrors],
  data() {
    return {
      //   tab: "system",
      // tab: "user-setting",
      tab: "system",
      //   tab: "general",
      angles: [1, 2, 5, 10, 15, 30, 45, 60, 90, 180],
      systemLanguages: [
        {
          code: "az",
          name: "Azərbaycan dili",
        },
        {
          code: "en",
          name: "English",
        },
        {
          code: "hy",
          name: "հայերեն",
        },
        {
          code: "be",
          name: "Беларускі",
        },
        {
          code: "ka",
          name: "ქართველი",
        },
        {
          code: "es",
          name: "Español",
        },
        {
          code: "kk",
          name: "Қазақ",
        },
        {
          code: "ky",
          name: "Кыргызча",
        },
        {
          code: "lv",
          name: "Latviešuл",
        },
        {
          code: "lt",
          name: "Lietuvis",
        },
        {
          code: "mol",
          name: "Moldovean",
        },
        {
          code: "mn",
          name: "Монгол",
        },
        {
          code: "de",
          name: "Deutsche",
        },
        {
          code: "ru",
          name: "Русский",
        },
        {
          code: "tg",
          name: "Тоҷикӣ",
        },
        {
          code: "tt",
          name: "Татар",
        },
        {
          code: "tk",
          name: "Türkmen",
        },
        {
          code: "uz",
          name: "O`zbek tili",
        },
        {
          code: "uk",
          name: "Український",
        },
        {
          code: "fr",
          name: "Français",
        },
        {
          code: "fi",
          name: "Suomalainen",
        },
        {
          code: "cs",
          name: "Český",
        },
        {
          code: "cv",
          name: "Чӑвашла",
        },
        {
          code: "et",
          name: "Eesti",
        },
      ],
      timezones: [
        {
          name: this.$t("admin.views.settings.timezones.moscow"),
          code: "Europe/Moscow",
          timezone: "UTC+3",
        },
        {
          name: this.$t("admin.views.settings.timezones.kaliningrad"),
          code: "Europe/Kaliningrad",
          timezone: "UTC+2",
        },
        {
          name: this.$t("admin.views.settings.timezones.samara"),
          code: "Europe/Samara",
          timezone: "UTC+4",
        },
        {
          name: this.$t("admin.views.settings.timezones.yekaterinburg"),
          code: "Asia/Yekaterinburg",
          timezone: "UTC+2",
        },
        {
          name: this.$t("admin.views.settings.timezones.omsk"),
          code: "Asia/Omsk",
          timezone: "UTC+6",
        },
        {
          name: this.$t("admin.views.settings.timezones.krasnoyarsk"),
          code: "Asia/Krasnoyarsk",
          timezone: "UTC+7",
        },
        {
          name: this.$t("admin.views.settings.timezones.irkutsk"),
          code: "Asia/Irkutsk",
          timezone: "UTC+8",
        },
        {
          name: this.$t("admin.views.settings.timezones.yakutsk"),
          code: "Asia/Yakutsk",
          timezone: "UTC+9",
        },
        {
          name: this.$t("admin.views.settings.timezones.vladivostok"),
          code: "Asia/Vladivostok",
          timezone: "UTC+10",
        },
        {
          name: this.$t("admin.views.settings.timezones.magadan"),
          code: "Asia/Magadan",
          timezone: "UTC+11",
        },
        {
          name: this.$t("admin.views.settings.timezones.kamchatka"),
          code: "Asia/Kamchatka",
          timezone: "UTC+12",
        },
        {
          name: this.$t("admin.views.settings.timezones.baku"),
          code: "Asia/Baku",
          timezone: "UTC+4",
        },
        {
          name: this.$t("admin.views.settings.timezones.yerevan"),
          code: "Asia/Yerevan",
          timezone: "UTC+4",
        },
        {
          name: this.$t("admin.views.settings.timezones.minsk"),
          code: "Europe/Minsk",
          timezone: "UTC+3",
        },
        {
          name: this.$t("admin.views.settings.timezones.almaty"),
          code: "Asia/Almaty",
          timezone: "UTC+6",
        },
        {
          name: this.$t("admin.views.settings.timezones.bishkek"),
          code: "Asia/Bishkek",
          timezone: "UTC+6",
        },
        {
          name: this.$t("admin.views.settings.timezones.kamchatka2"),
          code: "Europe/Moscow/Kamchatka",
          timezone: "UTC+3",
        },
        {
          name: this.$t("admin.views.settings.timezones.dushanbe"),
          code: "Asia/Dushanbe",
          timezone: "UTC+5",
        },
        {
          name: this.$t("admin.views.settings.timezones.ashgabat"),
          code: "Asia/Ashgabat",
          timezone: "UTC+5",
        },
        {
          name: this.$t("admin.views.settings.timezones.tashkent"),
          code: "Asia/Tashkent",
          timezone: "UTC+5",
        },
        {
          name: this.$t("admin.views.settings.timezones.kiev"),
          code: "Europe/Kiev",
          timezone: "UTC+3",
        },
        {
          name: this.$t("admin.views.settings.timezones.helsinki"),
          code: "Europe/Helsinki",
          timezone: "UTC+3",
        },
        {
          name: this.$t("admin.views.settings.timezones.riga"),
          code: "Europe/Riga",
          timezone: "UTC+3",
        },
        {
          name: this.$t("admin.views.settings.timezones.vilnius"),
          code: "Europe/Vilnius",
          timezone: "UTC+3",
        },
        {
          name: this.$t("admin.views.settings.timezones.tallinn"),
          code: "Europe/Tallinn",
          timezone: "UTC+3",
        },
        {
          name: this.$t("admin.views.settings.timezones.bucharest"),
          code: "Europe/Bucharest",
          timezone: "UTC+3",
        },
        {
          name: this.$t("admin.views.settings.timezones.sofia"),
          code: "Europe/Sofia",
          timezone: "UTC+3",
        },
        {
          name: this.$t("admin.views.settings.timezones.warsaw"),
          code: "Europe/Warsaw",
          timezone: "UTC+2",
        },
        {
          name: this.$t("admin.views.settings.timezones.prague"),
          code: "Europe/Prague",
          timezone: "UTC+2",
        },
        {
          name: this.$t("admin.views.settings.timezones.bratislava"),
          code: "Europe/Bratislava",
          timezone: "UTC+2",
        },
        {
          name: this.$t("admin.views.settings.timezones.budapest"),
          code: "Europe/Budapest",
          timezone: "UTC+2",
        },
        {
          name: this.$t("admin.views.settings.timezones.berlin"),
          code: "Europe/Berlin",
          timezone: "UTC+2",
        },
        {
          name: this.$t("admin.views.settings.timezones.london"),
          code: "Europe/London",
          timezone: "UTC+1",
        },
        {
          name: this.$t("admin.views.settings.timezones.paris"),
          code: "Europe/Paris",
          timezone: "UTC+2",
        },
        {
          name: this.$t("admin.views.settings.timezones.madrid"),
          code: "Europe/Madrid",
          timezone: "UTC+2",
        },
      ],
      fastAccessButtons: [
        {
          title: this.$t("admin.views.settings.fast_access_add_stela"),
          code: "fast_access_add_stela",
          link: "/admin/elements/stely/add",
          image: "/images/fast-access/bd-stela.svg",
        },
        {
          title: this.$t(
            "admin.views.settings.fast_access_add_engraving_christianstvo"
          ),
          code: "fast_access_add_engraving_christianstvo",
          link: "/admin/engravings/christianstvo/add",
          image: "/images/fast-access/bd-grav-hristianstvo.svg",
        },
        {
          title: this.$t("admin.views.settings.fast_access_add_cvetnik"),
          code: "fast_access_add_cvetnik",
          link: "/admin/elements/cvetniki-nadgrobiya/add",
          image: "/images/fast-access/bd-nadgrobie.svg",
        },
        {
          title: this.$t(
            "admin.views.settings.fast_access_add_engraving_islam"
          ),
          code: "fast_access_add_engraving_islam",
          link: "/admin/engravings/islam/add",
          image: "/images/fast-access/bd-islam.svg",
        },
        {
          title: this.$t("admin.views.settings.fast_access_add_material"),
          code: "fast_access_add_material",
          link: "/admin/materials/add",
          image: "/images/fast-access/bd-material.svg",
        },
        {
          title: this.$t(
            "admin.views.settings.fast_access_add_engraving_other"
          ),
          code: "fast_access_add_engraving_other",
          link: "/admin/engravings/other/add",
          image: "/images/fast-access/bd-grav-prochee.svg",
        },
        {
          title: this.$t(
            "admin.views.settings.fast_access_add_engraving_portrait"
          ),
          code: "fast_access_add_engraving_portrait",
          link: "/admin/engravings/portraits/add",
          image: "/images/fast-access/bd-grav-portret.svg",
        },
        {
          title: this.$t("admin.views.settings.fast_access_add_barrier"),
          code: "fast_access_add_barrier",
          link: "/admin/barriers/add",
          image: "/images/fast-access/bd-ograda.svg",
        },
      ],
      elementsCatalog: [
        {
          title: this.$t("admin.views.settings.elements.stely"),
          code: "stely",
        },
        {
          title: this.$t("admin.views.settings.elements.tumby"),
          code: "tumby",
        },
        {
          title: this.$t("admin.views.settings.elements.cvetniki_nadgrobiya"),
          code: "cvetniki-nadgrobiya",
        },
        {
          title: this.$t("admin.views.settings.elements.vazy"),
          code: "vazy",
        },
        {
          title: this.$t("admin.views.settings.elements.stoliki"),
          code: "stoliki",
        },
        {
          title: this.$t("admin.views.settings.elements.skameiki"),
          code: "skameiki",
        },
        {
          title: this.$t("admin.views.settings.elements.barriers"),
          code: "barriers",
        },
      ],
      landscapingsCatalog: [
        {
          title: this.$t("admin.views.settings.landscapingsCatalog.plate_fill"),
          code: "plate-fill",
        },
        {
          title: this.$t("admin.views.settings.landscapingsCatalog.stripe"),
          code: "stripe",
        },
        {
          title: this.$t(
            "admin.views.settings.landscapingsCatalog.plate_ready"
          ),
          code: "plate-ready",
        },
        {
          title: this.$t("admin.views.settings.landscapingsCatalog.paving"),
          code: "paving",
        },
        {
          title: this.$t("admin.views.settings.landscapingsCatalog.grass"),
          code: "grass",
        },
        {
          title: this.$t("admin.views.settings.landscapingsCatalog.pour"),
          code: "pour",
        },
      ],
      engravingsCatalog: [
        {
          title: this.$t("admin.views.settings.engravings.eng_text"),
          code: "eng_text",
        },
        {
          title: this.$t("admin.views.settings.engravings.portraits"),
          code: "portraits",
        },
        {
          title: this.$t("admin.views.settings.engravings.christianstvo"),
          code: "christianstvo",
        },
        {
          title: this.$t("admin.views.settings.engravings.islam"),
          code: "islam",
        },
        {
          title: this.$t("admin.views.settings.engravings.other"),
          code: "other",
        },
      ],
      currencies: [
        { name: "RUB", code: "RUB", symbol: "₽" },
        { name: "AZN", code: "AZN", symbol: "₼" },
        { name: "AMD", code: "AMD", symbol: "֏" },
        { name: "BYN", code: "BYN", symbol: "Br" },
        { name: "KZT", code: "KZT", symbol: "₸" },
        { name: "KGS", code: "KGS", symbol: "сом" },
        { name: "LEU", code: "LEU", symbol: "L" },
        { name: "TJS", code: "TJS", symbol: "с." },
        { name: "UZS", code: "UZS", symbol: "so’m" },
        { name: "TMT", code: "TMT", symbol: "T" },
        { name: "UAH", code: "UAH", symbol: "₴" },
        { name: "EUR", code: "EUR", symbol: "€" },
        { name: "ALL", code: "ALL", symbol: "L" },
        { name: "BGN", code: "BGN", symbol: "лв" },
        { name: "BAM", code: "BAM", symbol: "KM" },
        { name: "GBP", code: "GBP", symbol: "£" },
        { name: "HUF", code: "HUF", symbol: "Ft" },
        { name: "DKK", code: "DKK", symbol: "kr" },
        { name: "ISK", code: "ISK", symbol: "kr" },
        { name: "MKD", code: "MKD", symbol: "ден" },
        { name: "MDL", code: "MDL", symbol: "L" },
        { name: "NOK", code: "NOK", symbol: "kr" },
        { name: "PLN", code: "PLN", symbol: "zl" },
        { name: "RON", code: "RON", symbol: "L" },
        { name: "RSD", code: "RSD", symbol: "din" },
        { name: "TRY", code: "TRY", symbol: "¤" },
        { name: "HRK", code: "HRK", symbol: "kn" },
        { name: "CZK", code: "CZK", symbol: "Kc" },
        { name: "CHF", code: "CHF", symbol: "Fr" },
        { name: "SEK", code: "SEK", symbol: "kr" },
        { name: "USD", code: "USD", symbol: "$" },
        { name: "LTL", code: "LTL", symbol: "Lt" },
      ],
      settingImages: {
        env_px: null,
        env_py: null,
        env_pz: null,
        env_nx: null,
        env_ny: null,
        env_nz: null,
        plot_texture: null,
        filler_cvetnik_texture: null,
      },
      form: {
        company_name: "",
        company_inn: "",
        company_address: "",
        company_phone: "",
        company_phone_cities: "",
        company_email: "",
        company_site: "",
        language: "ru",
        system_name: "",
        timezone: "Europe/Moscow",
        per_page: 25,
        request_time_limit: 90,
        admin_path: "admin",
        login: "test@test.test",
        password: "",
        password_confirmation: "",
        mail_send_to_client_enabled: 1,
        email_notification: "",
        email_notification_additional: [],
        send_copy_order_for_client: 1,
        count_elements_configurator: 24,
        count_elements_engravings: 36,
        size_3d_environment: 20,
        env_3d_texture: 0,
        plot_texture: 0,
        filler_cvetnik_texture: 0,
        sender: "Admin",
        email_site: "",
        email_title: "",
        email_theme: "",
        mail_transport: "php_mail",
        sendmail_path: "/usr/sbin/sendmail -bs",
        smtp_host: "",
        smtp_port: 25,
        smtp_encryption: "none",
        smtp_auth: 0,
        smtp_username: "",
        smtp_password: "",
        mail_test_customer_email: "",
        show_phone_for_client: 1,
        phone_for_client: "+7(888) 999-00-99",
        link_to_site_for_client: "http://kpam3d.ru",
        target_link_to_site_for_client: "_self",
        show_link_to_video_instruction: 1,
        show_info_pages: 1,
        show_button_pdf_order: 1,
        show_informer: 1,
        fast_access: [],
        elements_catalog: [],
        landscapings_catalog: [],
        engravings_catalog: [],
        calc_cost_price: 1,
        prepaid: 1,
        prepaid_type: "fix",
        prepaid_type_fix_value: 0,
        prepaid_type_percent_total_price_value: 0,
        prepaid_type_percent_total_cost_value: 0,
        with_calc_price: 1,
        winter_storage: 1,
        use_landscaping: 1,
        use_engraving: 1,
        sale: "no",
        constructor_sale_with_order_percent_value: 0,
        constructor_sale_with_order_percent_from_value: 0,
        constructor_sale_with_order_total_from_value: 0,
        constructor_sale_with_order_percent_season_value: 0,
        constructor_sale_with_order_percent_season_value_start_date: "",
        constructor_sale_with_order_percent_season_value_finish_date: "",
        shipping: 1,
        currency: "RUB",
        show_conditions_storage: 0,
        show_conditions_storage_in_pdf: 0,
        conditions_storage_value: "",
        show_conditions_shipping: 0,
        show_conditions_shipping_in_pdf: 0,
        conditions_shipping_value: "",
        show_field_shipping_info: 0,
        field_shipping_info_required: 0,
        label_field_shipping_info: "",
        show_form_confirm_order_name: 1,
        required_form_confirm_order_name: 0,
        show_form_confirm_order_phone: 1,
        required_form_confirm_order_phone: 0,
        show_form_confirm_order_email: 1,
        required_form_confirm_order_email: 0,
        show_form_confirm_order_comment: 1,
        required_form_confirm_order_comment: 0,
        informer_approximate_price: 0,
        informer_approximate_price_value: "",
        show_contacts_block: 1,
        show_contact_company: 1,
        show_contact_inn: 1,
        show_contact_address: 1,
        show_contact_phone: 1,
        show_contact_phone_city: 1,
        show_contact_email: 1,
        show_performance_regim: 1,
        default_active_performance_regim: 0,
        show_change_env: 1,
        default_no_env: 0,
        show_delete_button_client_confirm_order: 1,
        rotate_angle: 1,
        plot_mode: "list", //custom, list_and_custom
        plot_length_min: 150,
        plot_length_max: 250,
        plot_width_min: 150,
        plot_width_max: 250,
        plot_length_default: 250,
        plot_width_default: 250,
      },
    };
  },
  computed: {
    settings() {
      const settings = this.$store.state.settings.settings;
      for (const key in settings) {
        if (Object.hasOwnProperty.call(this.form, key)) {
          if (key == "password" || key == "password_confirmation") continue;
          this.form[key] = settings[key];
        }
      }
      return settings;
    },
    setSettingImages() {
      return this.$store.state.settings.settingImages;
    },
    perPages() {
      return this.$store.state.settings.perPages;
    },
    licensed() {
      return this.$store.getters["licensed"];
    },
  },
  created() {
    this.$store.dispatch("settings/fetchAdmin");
  },
  methods: {
    addEmailNotificationRecipient() {
      if (!Array.isArray(this.form.email_notification_additional)) {
        this.form.email_notification_additional = [];
      }
      if (this.form.email_notification_additional.length >= 5) {
        return;
      }
      this.form.email_notification_additional.push("");
    },
    removeEmailNotificationRecipient(index) {
      if (!Array.isArray(this.form.email_notification_additional)) {
        return;
      }
      this.form.email_notification_additional.splice(index, 1);
    },
    sendTestMail() {
      const customer_email =
        this.form.mail_test_customer_email &&
        String(this.form.mail_test_customer_email).trim() !== ""
          ? this.form.mail_test_customer_email
          : null;
      this.$store.dispatch("settings/sendTestMail", { customer_email });
    },
    selectFile(file, dataProps) {
      this.settingImages[dataProps] = file;
    },
    removeFile(dataProps) {
      this.settingImages[dataProps] = null;
      this.setSettingImages[dataProps] = null;
    },
    setTab(type) {
      this.tab = type;
    },
    saveData() {
      if (
        this.form.plot_mode == "custom" ||
        this.form.plot_mode == "list_and_custom"
      ) {
        if (
          parseInt(this.form.plot_length_min) < 1 ||
          !this.form.plot_length_min ||
          parseInt(this.form.plot_length_max) < 1 ||
          !this.form.plot_length_max ||
          parseInt(this.form.plot_width_min) < 1 ||
          !this.form.plot_width_min ||
          parseInt(this.form.plot_width_max) < 1 ||
          !this.form.plot_width_max ||
          parseInt(this.form.plot_length_default) < 1 ||
          !this.form.plot_length_default ||
          parseInt(this.form.plot_width_default) < 1 ||
          !this.form.plot_width_default
        ) {
          alert(this.$t("admin.views.settings.plot_error_message"));
          return false;
        }
      }

      if (parseFloat(this.form["size_3d_environment"]) > 40) {
        this.form["size_3d_environment"] = 40;
      }
      if (parseFloat(this.form["size_3d_environment"]) < 5) {
        this.form["size_3d_environment"] = 5;
      }

      if (parseInt(this.form.env_3d_texture) == 2) {
        if (
          (!this.settingImages.env_px && !this.setSettingImages.env_px) ||
          (!this.settingImages.env_py && !this.setSettingImages.env_py) ||
          (!this.settingImages.env_pz && !this.setSettingImages.env_pz) ||
          (!this.settingImages.env_nx && !this.setSettingImages.env_nx) ||
          (!this.settingImages.env_ny && !this.setSettingImages.env_ny) ||
          (!this.settingImages.env_nz && !this.setSettingImages.env_nz)
        ) {
          alert(this.$t("admin.views.settings.need_all_faces_for_3d"));
          return false;
        }

        if (
          this.settingImages.env_px ||
          this.settingImages.env_py ||
          this.settingImages.env_pz ||
          this.settingImages.env_nx ||
          this.settingImages.env_ny ||
          this.settingImages.env_nz
        ) {
          const imgDataPost = new FormData();

          if (this.settingImages.env_px) {
            imgDataPost.append("env_px", this.settingImages.env_px);
          }
          if (this.settingImages.env_py) {
            imgDataPost.append("env_py", this.settingImages.env_py);
          }
          if (this.settingImages.env_pz) {
            imgDataPost.append("env_pz", this.settingImages.env_pz);
          }
          if (this.settingImages.env_nx) {
            imgDataPost.append("env_nx", this.settingImages.env_nx);
          }
          if (this.settingImages.env_ny) {
            imgDataPost.append("env_ny", this.settingImages.env_ny);
          }
          if (this.settingImages.env_nz) {
            imgDataPost.append("env_nz", this.settingImages.env_nz);
          }

          this.$store.dispatch("settings/saveSettingImages", imgDataPost);
        }
      }

      if (parseInt(this.form.plot_texture) == 2) {
        if (
          !this.settingImages.plot_texture &&
          !this.setSettingImages.plot_texture
        ) {
          alert(this.$t("admin.views.settings.need_plot_texture"));
          return false;
        }
        if (this.settingImages.plot_texture) {
          const imgDataPost = new FormData();
          imgDataPost.append("plot_texture", this.settingImages.plot_texture);
          this.$store.dispatch("settings/saveSettingImages", imgDataPost);
        }
      }

      if (parseInt(this.form.filler_cvetnik_texture) == 2) {
        if (
          !this.settingImages.filler_cvetnik_texture &&
          !this.setSettingImages.filler_cvetnik_texture
        ) {
          alert(this.$t("admin.views.settings.need_filler_texture"));
          return false;
        }
        if (this.settingImages.filler_cvetnik_texture) {
          const imgDataPost = new FormData();
          imgDataPost.append(
            "filler_cvetnik_texture",
            this.settingImages.filler_cvetnik_texture
          );
          this.$store.dispatch("settings/saveSettingImages", imgDataPost);
        }
      }

      this.$store.dispatch("settings/setSettings", this.form);
    },
    addFastAccess(fab) {
      this.form.fast_access.push(fab);
    },
    removeFastAccess(code) {
      this.form.fast_access = this.form.fast_access.filter(
        (fa) => fa.code !== code
      );
    },
    addClientBlock(section, block) {
      this.form[section].push(block);
    },
    removeClientBlock(section, code) {
      this.form[section] = this.form[section].filter((fa) => fa.code !== code);
    },
  },
  watch: {
    settings: function (settings) {
      for (const key in settings) {
        if (Object.hasOwnProperty.call(this.form, key)) {
          if (key == "password" || key == "password_confirmation") continue;
          this.form[key] = settings[key];
        }
      }
    },
  },
};
</script>

<style scoped>
.settings-sticky {
  position: sticky;
  top: var(--admin-topbar-height, 0px);
  z-index: 70;
  background: #fff;
  padding: 10px 0;
}
.settings-sticky__bar {
  display: flex;
  align-items: center;
}
.settings-sticky .tabs {
  margin-top: 10px;
}
</style>

<style scoped>
.env__faces_images {
  margin-top: 5px;
  margin-left: 22px;
}
.env__faces {
  display: flex;
  flex-direction: column;
  row-gap: 15px;
}

.env__face {
  display: flex;
  align-items: center;
  column-gap: 7px;
}
.env__face-label {
  margin-right: 3px;
}
.marketing_settings {
  width: 80%;
}
.marketing_settings__buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 7px;
}
.marketing_settings__textarea {
  padding-left: 30px;
}
.fields_form_confirm_order__title {
  font-weight: 600;
  margin-bottom: 15px;
}
.fields_form_confirm_order__list {
  display: flex;
  flex-direction: column;
  row-gap: 15px;
  width: 50%;
}
.fields_form_confirm_order__item {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 15px;
}
</style>
